import { useMemo, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { FieldLabel, Tag } from '@scripsteam/scrips-signal-ds';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { AnchoredPopover, AnchoredPopoverContent, AnchoredPopoverTrigger } from '@scripsteam/scrips-signal-ds';
import { CountryDialPicker } from '@scripsteam/scrips-signal-ds';
import { useDebounce } from '@/shared/hooks/use-debounce';
import {
  useRolesByType,
  useSpecialityCodeList,
} from '@/settings/services';
import type { StaffSpeciality } from '@/types/settings-api';
import { cn } from '@/lib/utils';
import { SETTINGS_SECTION_TITLE } from '@/settings/components/settings-look';

/**
 * Create Practitioner inline form (PROD-896).
 *
 * Renders at `/settings/practitioner/new`. Mirrors the wizard's
 * `PractitionerAddSheet` STEP 1 — Flutter `add_doctor_widget.dart`
 * (parity locked in PROD-872 PR #323).
 *
 * Difference from the wizard sheet:
 *   - This is a PAGE, not a Sheet. No 2-step indicator (working hours
 *     are configured later from Settings → Schedule).
 *   - Page-level "Send Invite" button lives in `PractitionerEditShell`
 *     chrome; this form just renders fields + emits draft changes.
 *
 * Draft contract: the parent `PractitionerEditPage` owns the draft and
 * the mutation. This component is pure presentation + validation.
 */
export interface CreatePractitionerDraft {
  firstName: string;
  lastName: string;
  emailId: string;
  /** CountryDialPicker wire form, e.g. `"+971-554654645"`. */
  mobileNumber: string;
  role: number | null;
  doctorSpecialities: StaffSpeciality[];
}

export const EMPTY_CREATE_DRAFT: CreatePractitionerDraft = {
  firstName: '',
  lastName: '',
  emailId: '',
  mobileNumber: '+971-',
  role: null,
  doctorSpecialities: [],
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// E.164 caps total digits at 15 (ITU-T spec). We also enforce a soft floor
// of 7 to reject obvious placeholders. The CountryDialPicker writes
// `+CC-digits`; this guard counts only the digit half. Mirror Flutter
// `add_doctor_widget.dart` validation, which gates Save on a non-blank
// phone digits component without strict E.164 — we tighten the upper
// bound here per Samer review on PR #340.
const PHONE_MIN_DIGITS = 7;
const PHONE_MAX_DIGITS = 15;

export function isCreatePractitionerDraftValid(
  draft: CreatePractitionerDraft,
): boolean {
  const phoneDigits = (draft.mobileNumber.split('-')[1] ?? '').replace(
    /\D/g,
    '',
  );
  return (
    draft.firstName.trim().length > 0 &&
    draft.lastName.trim().length > 0 &&
    EMAIL_REGEX.test(draft.emailId.trim()) &&
    phoneDigits.length >= PHONE_MIN_DIGITS &&
    phoneDigits.length <= PHONE_MAX_DIGITS &&
    draft.role !== null &&
    draft.doctorSpecialities.length > 0
  );
}

export interface CreatePractitionerFormProps {
  draft: CreatePractitionerDraft;
  onChange: (next: CreatePractitionerDraft) => void;
  /** Organisation id from auth-store. Used to fetch the role dropdown. */
  organizationId: string | undefined;
  disabled?: boolean;
}

export function CreatePractitionerForm({
  draft,
  onChange,
  organizationId,
  disabled = false,
}: CreatePractitionerFormProps) {
  const { data: roleRows = [] } = useRolesByType(3, organizationId);

  const roleOptions = useMemo(
    () =>
      roleRows
        .filter((r) => !r.isArchived)
        .map((r) => ({ value: r.roleId, label: r.roleName })),
    [roleRows],
  );

  return (
    <div className="space-y-6">
      <div className="rounded-lg bg-surface-background p-4 text-center">
        <p className="text-base font-bold text-brand-primary">
          You&rsquo;re Creating An Account For A Practitioner
        </p>
        <p className="mt-2 text-sm text-text-muted">
          After clicking &lsquo;Send Invite&rsquo; Scrips will send an invite to
          this user so they can log into the practice and add their details
        </p>
      </div>

      <section>
        <h3 className={`${SETTINGS_SECTION_TITLE} mb-3`}>
          Practitioner Details
        </h3>
        <div className="rounded-lg border border-border-hairline bg-surface-card p-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
          <FieldText
            label="First Name"
            required
            value={draft.firstName}
            disabled={disabled}
            onChange={(v) => onChange({ ...draft, firstName: v })}
          />
          <FieldText
            label="Last Name"
            required
            value={draft.lastName}
            disabled={disabled}
            onChange={(v) => onChange({ ...draft, lastName: v })}
          />
          <SpecialityField
            value={draft.doctorSpecialities}
            disabled={disabled}
            onChange={(next) =>
              onChange({ ...draft, doctorSpecialities: next })
            }
          />
          <RoleField
            options={roleOptions}
            value={draft.role}
            disabled={disabled}
            onChange={(v) => onChange({ ...draft, role: v })}
          />
        </div>
      </section>

      <section>
        <h3 className={`${SETTINGS_SECTION_TITLE} mb-3`}>
          Account Details
        </h3>
        <div className="rounded-lg border border-border-hairline bg-surface-card p-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
          <FieldText
            label="Email Address"
            required
            type="email"
            value={draft.emailId}
            disabled={disabled}
            onChange={(v) => onChange({ ...draft, emailId: v })}
          />
          <div className="flex flex-col gap-2">
            <FieldLabel required>Contact Number</FieldLabel>
            <CountryDialPicker
              value={draft.mobileNumber}
              onChange={(v) => onChange({ ...draft, mobileNumber: v })}
              disabled={disabled}
              size="lg"
            />
          </div>
        </div>
      </section>
    </div>
  );
}

function FieldText({
  label,
  required,
  value,
  onChange,
  disabled,
  type = 'text',
}: {
  label: string;
  required?: boolean;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  type?: 'text' | 'email';
}) {
  return (
    <div className="flex flex-col gap-2">
      <FieldLabel required={required}>{label}</FieldLabel>
      <TextField
        type={type}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        inputClassName="h-10"
      />
    </div>
  );
}


function RoleField({
  options,
  value,
  onChange,
  disabled,
}: {
  options: Array<{ value: number; label: string }>;
  value: number | null;
  onChange: (v: number | null) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);

  return (
    <div className="flex flex-col gap-2">
      <FieldLabel required>Role</FieldLabel>
      <AnchoredPopover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
        <AnchoredPopoverTrigger
          render={
            <button
              type="button"
              disabled={disabled}
              className={cn(
                'h-10 flex items-center justify-between rounded-md border border-border-hairline-strong bg-surface-card px-3 text-sm disabled:opacity-60 disabled:cursor-not-allowed',
                selected ? 'text-text-default' : 'text-text-hint',
              )}
            />
          }
        >
          <span className="truncate">{selected?.label ?? 'Select role'}</span>
          {selected ? (
            <span
              role="img"
              aria-label="Clear role"
              className="inline-flex text-text-muted hover:text-text-default cursor-pointer flex-shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                if (!disabled) onChange(null);
              }}
            >
              <X className="size-4" aria-hidden />
            </span>
          ) : <ChevronDown className="size-4 text-text-secondary flex-shrink-0" />}
        </AnchoredPopoverTrigger>
        <AnchoredPopoverContent collisionAvoidance={{ fallbackAxisSide: 'end' }}
          align="start"
          className="w-anchor p-1 gap-3"
        >
          {options.length === 0 ? (
            <p className="px-3 py-2 text-sm text-text-muted">
              No roles available
            </p>
          ) : (
            options.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full items-center px-3 py-2 text-left text-sm rounded',
                  o.value === value
                    ? 'bg-blue-100 text-brand-depth'
                    : 'hover:bg-surface-background text-text-default',
                )}
              >
                {o.label}
              </button>
            ))
          )}
        </AnchoredPopoverContent>
      </AnchoredPopover>
    </div>
  );
}

function SpecialityField({
  value,
  onChange,
  disabled,
}: {
  value: StaffSpeciality[];
  onChange: (next: StaffSpeciality[]) => void;
  disabled?: boolean;
}) {
  const [query, setQuery] = useState('');
  const debounced = useDebounce(query, 300);
  const [open, setOpen] = useState(false);
  const { data: results = [], isLoading } = useSpecialityCodeList(debounced);

  const filtered = results.filter(
    (r) =>
      !value.some((v) => v.specialitySkillId === r.specialitySkillId),
  );

  const handlePick = (r: StaffSpeciality) => {
    onChange([...value, r]);
    setQuery('');
  };

  const handleRemove = (skillId: string) => {
    onChange(value.filter((v) => v.specialitySkillId !== skillId));
  };

  return (
    <div className="flex flex-col gap-2 sm:col-span-2">
      <FieldLabel required>Speciality</FieldLabel>
      <AnchoredPopover
        open={open && debounced.length > 0 && !disabled}
        onOpenChange={(o) => !disabled && setOpen(o)}
      >
        <AnchoredPopoverTrigger
          render={
            <div
              className={cn(
                'flex flex-wrap items-center gap-2 rounded-md border border-border-hairline-strong bg-surface-card px-2 py-1.5 min-h-10 focus-within:ring-2 focus-within:ring-brand-primary/30',
                disabled && 'opacity-60 cursor-not-allowed',
              )}
              onClick={() => !disabled && setOpen(true)}
            />
          }
        >
          {value.map((s) => (
            <Tag
              key={s.specialitySkillId}
              onRemove={
                disabled ? undefined : () => handleRemove(s.specialitySkillId)
              }
              removeLabel={`Remove ${s.code.displayName}`}
            >
              {s.code.displayName ?? s.code.code}
            </Tag>
          ))}
          <input
            type="text"
            value={query}
            disabled={disabled}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            placeholder={value.length === 0 ? 'Enter speciality' : ''}
            className="flex-1 min-w-30 border-0 bg-transparent text-sm outline-none focus:ring-0 placeholder:text-text-hint disabled:opacity-60"
          />
        </AnchoredPopoverTrigger>
        <AnchoredPopoverContent collisionAvoidance={{ fallbackAxisSide: 'end' }}
          align="start"
          className="w-anchor p-1 max-h-70 overflow-y-auto scrollbar-hide gap-3"
        >
          {isLoading ? (
            <p className="px-3 py-2 text-sm text-text-muted">Searching…</p>
          ) : filtered.length === 0 ? (
            <p className="px-3 py-2 text-sm text-text-muted">No matches</p>
          ) : (
            filtered.map((r) => (
              <button
                key={r.specialitySkillId}
                type="button"
                onClick={() => handlePick(r)}
                className="flex w-full items-center px-3 py-2 text-left text-sm rounded hover:bg-surface-background text-text-default"
              >
                {r.code.displayName ?? r.code.code}
              </button>
            ))
          )}
        </AnchoredPopoverContent>
      </AnchoredPopover>
    </div>
  );
}
